import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiDataService } from 'src/app/shared/services/api.data.service';
import { IssueDto } from './models/issue';
import { DispositionRequest } from './models/disposition';
import { IssueCreateRequest } from './models/issue-create-request';
import { IssueSyncRow } from './sync/issue-sync-models';

// One page of closed Issues. `totalCount` stops at the server's cap, so a number that
// sits exactly on the cap reads as "at least".
export interface IssueClosedPage {
  items: IssueSyncRow[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// HTTP for the Issues v1 endpoints (main Web API only). Reads return plain
// arrays; all writes return 202 (applied asynchronously by a background
// function — see IssuesStateService for the optimistic/reconcile handling).
@Injectable({ providedIn: 'root' })
export class IssuesService {
  private readonly apiController = 'issues';

  constructor(private readonly api: ApiDataService) {}

  // GET /v1/{tenantId}/issues?assetId={vehicleId} — returns OPEN issues only
  // (the backend filters to Emerging/Active/Fading).
  listByAsset(assetId: string, skip: number = 0, take: number = 50): Observable<IssueDto[]> {
    const url = this.api.buildUrlWithParams(this.apiController, { assetId, skip, take });
    return this.api.get<IssueDto[]>(url);
  }

  getById(id: string): Observable<IssueDto> {
    const url = this.api.buildUrl(`${this.apiController}/${encodeURIComponent(id)}`);
    return this.api.get<IssueDto>(url);
  }

  // Operator-reported problem. 202 = published, not written: the report lands on
  // an existing open issue for the same vehicle+slug, or mints a new one. A 400
  // carries a string[] of validation messages meant to be shown to the operator.
  requestCreate(body: IssueCreateRequest): Observable<void> {
    return this.api.post<void>(this.api.buildUrl(this.apiController), body);
  }

  requestDisposition(id: string, body: DispositionRequest): Observable<void> {
    const url = this.api.buildUrl(`${this.apiController}/${encodeURIComponent(id)}/disposition`);
    return this.api.post<void>(url, body);
  }

  // GET /v1/{tenantId}/issues/closed — one page, newest close first. Served straight
  // from the server: closed rows never enter the local replica, which holds open work.
  closedPage(page: number, pageSize: number): Observable<IssueClosedPage> {
    const url = this.api.buildUrlWithParams(`${this.apiController}/closed`, { page, pageSize });
    return this.api.get<IssueClosedPage>(url);
  }
}
